import React from 'react'
import { cn } from '../shared/utils/cn'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBatteryEmpty,
  faBatteryQuarter,
  faBatteryFull,
  faCheck,
  faTimes,
  faThermometerHalf,
  faMapMarkerAlt,
  faPlug,
} from '@fortawesome/free-solid-svg-icons'

// Import converters from sigfox-decoder package
import { temperature as temperatureConverter } from '@atcreate-inc/sigfox-decoder/dist/converters/temperature'
import { battery as batteryConverter } from '@atcreate-inc/sigfox-decoder/dist/converters/battery'

// Import unit conversion utilities
import { convertValue, getUnitAbbreviation, getBaseUnitId } from '../utils/unitConversionUtils'
import { formatNumber } from '../common/utils'
import { UnitCategoryId } from '../common/enums'
import { UnitOverrides } from '../hooks/useMessageUnitOverrides'
import { convertSensorReading } from '../utils/sensorReadingConversion'

// Sensor config from API (keyed by port name)
interface SensorConfigMap {
  [portName: string]: {
    sensorConfigId?: number
    deviceId?: number
    sensorName?: string
    sensorProductId?: number // 1 = None (unconfigured)
    sensorNumber?: number
    unitCategoryId?: number
    unitId?: number
    conversionScale?: number
    conversionOffset?: number
  }
}

// Helper to handle invalid temperature readings (0xFFF = 4095)
const temperature = (value: number): number | null => {
  if (value === 0xfff || value === 4095) {
    return null // Invalid reading
  }
  return temperatureConverter(value)
}

// Helper for battery conversion
const battery = (value: number): number => {
  return batteryConverter(value)
}

interface ParsedMessageDisplayProps {
  messageContent: string
  messageType: string
  parsedData: any
  className?: string
  selectedSensorPorts?: Set<string>
  sensorConfigs?: SensorConfigMap
  unitOverrides?: UnitOverrides
}

export function ParsedMessageDisplay({
  messageContent,
  messageType,
  parsedData,
  className = '',
  selectedSensorPorts = new Set(),
  sensorConfigs = {},
  unitOverrides = {},
}: ParsedMessageDisplayProps) {
  // Helper to get sensor config by port name
  const getSensorConfig = (portName: string) => sensorConfigs[portName]

  // Handle different message types using direct decoder output
  const renderContent = () => {
    // Handle decoding errors (if decoder throws or returns null/undefined)
    if (!parsedData) {
      return <div className="text-red-600 text-xs font-medium">Unable to parse message</div>
    }

    switch (messageType) {
      case 'SCHD':
        return renderSchdMessage(parsedData)
      case 'TestMessage':
        return renderTestMessage(parsedData)
      case 'SNTS':
        return renderSntsMessage(parsedData)
      case 'DeviceConfig1':
      case 'DeviceConfig2':
      case 'DeviceConfig3':
      case 'DeviceConfig4':
      case 'DeviceConfig5':
        return renderDeviceConfigMessage(parsedData, messageType)
      case 'SensorConfig1':
        return renderSensorConfigMessage(parsedData)
      case 'DeviceConfigUpdated':
        return renderDeviceConfigUpdatedMessage(parsedData)
      case 'SensorConfigUpdated':
        return renderSensorConfigUpdatedMessage(parsedData)
      default:
        return renderGenericMessage(parsedData)
    }
  }

  // For sensors, we only show if configured - raw values can't be meaningfully converted
  const isSensorConfigured = (config: SensorConfigMap[string] | undefined): boolean => {
    return !!(config && config.sensorProductId && config.sensorProductId !== 1)
  }

  // Helper to get sensor display value with unit override support
  const getSensorDisplayValue = (
    rawValue: number,
    config: SensorConfigMap[string] | undefined
  ): { value: number; unit: string } | null => {
    if (!config || !isSensorConfigured(config)) return null
    if (!config.unitCategoryId || config.unitCategoryId <= 0) return null

    // Step 1: Convert raw to base unit with the product's converter (VH400 curve etc.)
    const scaledValue = convertSensorReading(rawValue, config)
    if (scaledValue === null) return null

    // Step 2: Check if there's a unit override for this category
    const overrideUnitId = unitOverrides[config.unitCategoryId]
    if (overrideUnitId !== undefined) {
      // Convert from base unit to override unit
      const convertedValue = convertValue(scaledValue, config.unitCategoryId, overrideUnitId)
      const unitAbbr = getUnitAbbreviation(config.unitCategoryId, overrideUnitId)
      return { value: convertedValue, unit: unitAbbr }
    }

    // No override - use base unit (or sensor's configured unit)
    const baseUnitId = getBaseUnitId(config.unitCategoryId)
    const unitAbbr = baseUnitId ? getUnitAbbreviation(config.unitCategoryId, baseUnitId) : ''
    return { value: scaledValue, unit: unitAbbr }
  }

  // Helper to get temperature display value with unit override support
  const getTemperatureDisplay = (rawTemp: number): { value: number; unit: string } | null => {
    const tempC = temperature(rawTemp)
    if (tempC === null) return null

    // Check if there's a unit override for temperature
    const overrideUnitId = unitOverrides[UnitCategoryId.Temperature]
    if (overrideUnitId !== undefined) {
      const convertedValue = convertValue(tempC, UnitCategoryId.Temperature, overrideUnitId)
      const unitAbbr = getUnitAbbreviation(UnitCategoryId.Temperature, overrideUnitId)
      return { value: convertedValue, unit: unitAbbr }
    }

    // Default to Celsius (base unit)
    return { value: tempC, unit: 'C' }
  }

  const renderSchdMessage = (data: any) => {
    if (!data) return <span className="text-gray-500 text-xs">No data available</span>

    const filteredSensorReadings = data.sensorReadings
      ? Object.entries(data.sensorReadings).filter(
          ([sensor]) => selectedSensorPorts.size === 0 || selectedSensorPorts.has(sensor)
        )
      : []

    return (
      <div className="space-y-1">
        {/* Row 1: Status Labels */}
        <div className="flex items-center gap-2 text-xs">
          <StatusLabel label="Config" active={data.flagRicInConfigMode} />
          <StatusLabel label="Startup" active={data.flagInScheduleStartupMode} />
          <StatusLabel label="Low Power" active={data.flagPowerIsBelowWarningLevel} type="warning" />
          <StatusLabel label="Shutdown" active={data.flagPowerIsBelowShutdownLevel} type="error" />
        </div>

        {/* Sensor Readings + Temperature: Show raw value | converted value | unit */}
        <div className="space-y-0.5 text-xs">
          {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((port) => {
            const entry = filteredSensorReadings.find(([s]) => s === port)
            if (!entry) return null
            const rawValue = entry[1] as number | undefined
            const hasRawValue = rawValue !== undefined && rawValue !== null
            const config = getSensorConfig(port)
            const sensorDisplay = hasRawValue ? getSensorDisplayValue(rawValue, config) : null

            return (
              <div key={port} className="flex items-center">
                <span className="w-14 font-medium text-gray-600">{port}:</span>
                {/* Show raw value */}
                <span className={`w-16 font-mono ${hasRawValue ? 'text-gray-500' : 'text-gray-400'}`}>
                  {hasRawValue ? rawValue : '—'}
                </span>
                {/* Show converted value and unit if sensor is configured */}
                {sensorDisplay && (
                  <>
                    <span className="font-mono text-gray-900 mr-1">{formatNumber(sensorDisplay.value, 2)}</span>
                    <span className="text-gray-500">{sensorDisplay.unit}</span>
                  </>
                )}
              </div>
            )
          })}
          {/* Temperature row - same format as sensors */}
          {data.temperature !== undefined && (
            <div className="flex items-center">
              <span className="w-14 font-medium text-gray-600">Temp:</span>
              <span className="w-16 font-mono text-gray-500">{data.temperature}</span>
              {(() => {
                const tempDisplay = getTemperatureDisplay(data.temperature)
                if (tempDisplay !== null) {
                  return (
                    <>
                      <span className="font-mono text-gray-900 mr-1">{formatNumber(tempDisplay.value, 1)}</span>
                      <span className="text-gray-500">{tempDisplay.unit}</span>
                    </>
                  )
                }
                return <span className="font-mono">—</span>
              })()}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderTestMessage = (data: any) => {
    if (!data) return <span className="text-gray-500 text-xs">No data available</span>

    return (
      <div className="text-xs">
        {data.message && <span className="text-gray-700 font-mono">{data.message.trim()}</span>}
      </div>
    )
  }

  const renderSntsMessage = (data: any) => {
    if (!data) return <span className="text-gray-500 text-xs">No data available</span>

    return (
      <div className="space-y-1">
        {/* Row 1: Power indicator + Battery readings */}
        <div className="flex items-center gap-2 text-xs">
          <PowerIndicator
            isShutdown={data.flagPowerIsBelowShutdownLevel}
            isWarning={data.flagPowerIsBelowWarningLevel}
          />
          {data.batteryReadingsEnabled && (
            <>
              {data.batteryReading !== undefined && (
                <>
                  <span className="text-gray-300">|</span>
                  <span>
                    <span className="text-gray-500">Batt:</span>{' '}
                    <span className="font-mono text-gray-900">{formatNumber(battery(data.batteryReading), 2)}V</span>
                  </span>
                </>
              )}
              {data.portCPowerReading !== undefined && (
                <span>
                  <span className="text-gray-500">PortC:</span>{' '}
                  <span className="font-mono text-gray-900">{formatNumber(battery(data.portCPowerReading), 2)}V</span>
                </span>
              )}
            </>
          )}
        </div>

        {/* Row 2: Sequence and timestamp */}
        <div className="flex items-center gap-3 text-xs">
          {data.sequenceNumber !== undefined && (
            <span>
              <span className="text-gray-500">Seq:</span>{' '}
              <span className="font-mono text-gray-900">{data.sequenceNumber}</span>
            </span>
          )}
          {data.timestamp !== undefined && (
            <span>
              <span className="text-gray-500">Time:</span>{' '}
              <span className="font-mono text-gray-900">
                {new Date((data.timestamp * 60 + 1514764800) * 1000).toLocaleString()}
              </span>
            </span>
          )}
        </div>
      </div>
    )
  }

  const renderDeviceConfigMessage = (data: any, messageType: string) => {
    if (!data) return <span className="text-gray-500 text-xs">No data available</span>

    return (
      <div className="space-y-1">
        {messageType === 'DeviceConfig1' && (
          <>
            {/* Row 1: Feature flags as icons */}
            <div className="flex items-center gap-2 text-xs">
              <div className="flex items-center gap-1">
                <CompactStatusIndicator
                  value={data.flagBatteryReadingEnabledInSnts}
                  icon={faBatteryFull}
                  activeColor="bg-green-100 text-green-600"
                  inactiveColor="bg-gray-100 text-gray-400"
                  tooltip={`Battery in SNTS: ${data.flagBatteryReadingEnabledInSnts ? 'Enabled' : 'Disabled'}`}
                />
                <CompactStatusIndicator
                  value={data.flagTemperatureReadingEnabledInSchd}
                  icon={faThermometerHalf}
                  activeColor="bg-green-100 text-green-600"
                  inactiveColor="bg-gray-100 text-gray-400"
                  tooltip={`Temperature in SCHD: ${data.flagTemperatureReadingEnabledInSchd ? 'Enabled' : 'Disabled'}`}
                />
              </div>
              <span className="text-gray-300">|</span>
              <span>
                <span className="text-gray-500">SNTS:</span> <span className="font-mono">{data.sntsFrequency}</span>
              </span>
              <span>
                <span className="text-gray-500">SCHD:</span> <span className="font-mono">{data.schdFrequency}</span>
              </span>
            </div>
            {/* Row 2: Location if present */}
            {data.coordinateType && (
              <div className="flex items-center gap-2 text-xs">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="h-3 w-3 text-gray-400" />
                <span className="font-mono">
                  {data.coordinateType}: {data.locationNSValue?.toFixed(4) || 'N/A'},{' '}
                  {data.locationEWValue?.toFixed(4) || 'N/A'}
                </span>
              </div>
            )}
          </>
        )}

        {messageType === 'DeviceConfig2' && (
          <div className="flex items-center gap-3 text-xs">
            <span>
              <span className="text-gray-500">FW:</span>{' '}
              <span className="font-mono font-medium">{data.firmwareVersion}</span>
            </span>
            {data.locationGridZone && (
              <span>
                <span className="text-gray-500">Zone:</span> <span className="font-mono">{data.locationGridZone}</span>
              </span>
            )}
            {data.locationZValue !== undefined && (
              <span>
                <span className="text-gray-500">Z:</span>{' '}
                <span className="font-mono">{data.locationZValue?.toFixed(4) || 'N/A'}</span>
              </span>
            )}
          </div>
        )}

        {messageType === 'DeviceConfig3' && (
          <div className="flex items-center gap-3 text-xs">
            <span>
              <span className="text-gray-500">PIN:</span> <span className="font-mono">{data.devicePin}</span>
            </span>
            <span>
              <span className="text-gray-500">Mode:</span> <span className="font-mono">{data.deviceAppMode}</span>
            </span>
          </div>
        )}

        {messageType === 'DeviceConfig4' && (
          <div className="flex items-center gap-2 text-xs">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="h-3 w-3 text-gray-400" />
            <span className="font-mono">{data.locationNamePart1 || '(empty)'}</span>
          </div>
        )}

        {messageType === 'DeviceConfig5' && (
          <div className="flex items-center gap-2 text-xs">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="h-3 w-3 text-gray-400" />
            <span className="font-mono">{data.locationNamePart2 || '(empty)'}</span>
          </div>
        )}

        {/* Fallback for unrecognized data */}
        {!['DeviceConfig1', 'DeviceConfig2', 'DeviceConfig3', 'DeviceConfig4', 'DeviceConfig5'].includes(
          messageType
        ) && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {Object.entries(data).map(([key, value]) => (
              <span key={key}>
                <span className="text-gray-500">{key}:</span> <span className="font-mono">{String(value)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderSensorConfigMessage = (data: any) => {
    if (!data) return <span className="text-gray-500 text-xs">No data available</span>

    return (
      <div className="space-y-1">
        {/* Row 1: Status + Port + Type */}
        <div className="flex items-center gap-2 text-xs">
          <CompactStatusIndicator
            value={data.flagSensorEnabled}
            icon={data.flagSensorEnabled ? faCheck : faTimes}
            activeColor="bg-green-100 text-green-600"
            inactiveColor="bg-red-100 text-red-400"
            tooltip={data.flagSensorEnabled ? 'Sensor Enabled' : 'Sensor Disabled'}
          />
          {data.sensorPort && (
            <>
              <span className="text-gray-300">|</span>
              <span>
                <span className="text-gray-500">Port:</span>{' '}
                <span className="font-mono font-medium">{data.sensorPort}</span>
              </span>
            </>
          )}
          {data.sensorNumber !== undefined && (
            <span>
              <span className="text-gray-500">#</span>
              <span className="font-mono">{data.sensorNumber}</span>
            </span>
          )}
          {data.sensorType && (
            <span className="font-mono text-gray-700 truncate max-w-[150px]" title={data.sensorType}>
              {data.sensorType}
            </span>
          )}
          {data.sensorUnits && <span className="text-gray-500">({data.sensorUnits})</span>}
        </div>

        {/* Row 2: Conversion info if present */}
        {data.conversionMethod && (
          <div className="flex items-center gap-3 text-xs">
            <span>
              <span className="text-gray-500">Conv:</span> <span className="font-mono">{data.conversionMethod}</span>
            </span>
            {data.conversionScale !== undefined && (
              <span>
                <span className="text-gray-500">×</span>
                <span className="font-mono">{data.conversionScale.toFixed(4)}</span>
              </span>
            )}
            {data.conversionOffset !== undefined && (
              <span>
                <span className="text-gray-500">+</span>
                <span className="font-mono">{data.conversionOffset.toFixed(4)}</span>
              </span>
            )}
          </div>
        )}

        {/* Fallback for unrecognized sensor config data */}
        {!data.sensorNumber && !data.sensorPort && !data.sensorType && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {Object.entries(data).map(([key, value]) => (
              <span key={key}>
                <span className="text-gray-500">{key}:</span> <span className="font-mono">{String(value)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderDeviceConfigUpdatedMessage = (data: any) => {
    if (!data || !data.aggregatedDeviceConfigs) return <span className="text-gray-500 text-xs">No aggregated data</span>

    // Extract key info from each config
    const configs = data.aggregatedDeviceConfigs
    const dc2 = configs.find((c: any) => c.messageType === 'DeviceConfig2')
    const dc3 = configs.find((c: any) => c.messageType === 'DeviceConfig3')
    const dc4 = configs.find((c: any) => c.messageType === 'DeviceConfig4')
    const dc5 = configs.find((c: any) => c.messageType === 'DeviceConfig5')

    const siteName = [dc4?.parsedData?.locationNamePart1, dc5?.parsedData?.locationNamePart2].filter(Boolean).join('')

    return (
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-gray-400">({configs.length} msgs)</span>
        {dc2?.parsedData?.firmwareVersion && (
          <span>
            <span className="text-gray-500">FW:</span>{' '}
            <span className="font-mono font-medium">{dc2.parsedData.firmwareVersion}</span>
          </span>
        )}
        {dc3?.parsedData?.deviceAppMode && (
          <span>
            <span className="text-gray-500">Mode:</span>{' '}
            <span className="font-mono">{dc3.parsedData.deviceAppMode}</span>
          </span>
        )}
        {siteName && (
          <span>
            <FontAwesomeIcon icon={faMapMarkerAlt} className="h-3 w-3 text-gray-400 mr-1" />
            <span className="font-mono">{siteName}</span>
          </span>
        )}
      </div>
    )
  }

  const renderSensorConfigUpdatedMessage = (data: any) => {
    if (!data || !data.aggregatedSensorConfigs) return <span className="text-gray-500 text-xs">No aggregated data</span>

    const sensors = data.aggregatedSensorConfigs

    return (
      <div className="space-y-1">
        {/* Row 1: Summary */}
        <div className="flex items-center gap-2 text-xs">
          <FontAwesomeIcon icon={faPlug} className="h-3 w-3 text-gray-400" />
          <span className="text-gray-500">({sensors.length} sensors)</span>
        </div>
        {/* Row 2: Sensor list in compact grid */}
        <div className="grid grid-cols-3 gap-x-4 gap-y-0.5 text-xs">
          {sensors.map((sensor: any, index: number) => (
            <span key={index}>
              <span className="font-medium text-gray-600">{sensor.parsedData?.sensorPort || `S${index + 1}`}:</span>{' '}
              <span className="font-mono text-gray-900 truncate" title={sensor.parsedData?.sensorType}>
                {sensor.parsedData?.sensorType?.substring(0, 15) || '?'}
                {sensor.parsedData?.sensorUnits && ` (${sensor.parsedData.sensorUnits})`}
              </span>
            </span>
          ))}
        </div>
      </div>
    )
  }

  const renderGenericMessage = (data: any) => {
    if (!data) return <span className="text-gray-500 text-xs">No parsed data</span>

    return (
      <div className="text-xs">
        {data.message && <span className="text-gray-700 mr-2">{data.message}</span>}
        {data.data && typeof data.data === 'object' && (
          <span className="inline-flex flex-wrap gap-x-3 gap-y-0.5">
            {Object.entries(data.data).map(([key, value]) => (
              <span key={key}>
                <span className="text-gray-500">{key}:</span> <span className="font-mono">{String(value)}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    )
  }

  return <div className={cn('w-full', className)}>{renderContent()}</div>
}

// Badge-style status label with consistent width
function StatusLabel({
  label,
  active,
  type = 'info',
}: {
  label: string
  active: boolean
  type?: 'info' | 'warning' | 'error'
}) {
  const activeStyles = {
    info: 'bg-blue-100 text-blue-700',
    warning: 'bg-yellow-100 text-yellow-700',
    error: 'bg-red-100 text-red-700',
  }

  return (
    <span
      className={cn(
        'text-xs px-1.5 py-0.5 rounded w-[4.5rem] text-center inline-block whitespace-nowrap',
        active ? activeStyles[type] : 'bg-gray-100 text-gray-400'
      )}
    >
      {label}
    </span>
  )
}

// Compact icon-based status indicator with tooltip
function CompactStatusIndicator({
  value,
  icon,
  activeIcon,
  activeColor,
  inactiveColor,
  tooltip,
}: {
  value: boolean
  icon: any
  activeIcon?: any
  activeColor: string
  inactiveColor: string
  tooltip: string
}) {
  const displayIcon = value && activeIcon ? activeIcon : icon
  return (
    <span className="relative group">
      <span
        className={cn(
          'inline-flex items-center justify-center w-5 h-5 rounded-full cursor-help',
          value ? activeColor : inactiveColor
        )}
      >
        <FontAwesomeIcon icon={displayIcon} className="h-3 w-3" />
      </span>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
        {tooltip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  )
}

// Combined power indicator showing battery state
function PowerIndicator({ isShutdown, isWarning }: { isShutdown: boolean; isWarning: boolean }) {
  const getContent = () => {
    if (isShutdown) {
      return {
        icon: faBatteryEmpty,
        className: 'bg-red-100 text-red-600 animate-pulse',
        tooltip: 'Power Shutdown (Critical)',
      }
    }
    if (isWarning) {
      return {
        icon: faBatteryQuarter,
        className: 'bg-yellow-100 text-yellow-600',
        tooltip: 'Low Battery Warning',
      }
    }
    return {
      icon: faBatteryFull,
      className: 'bg-green-100 text-green-600',
      tooltip: 'Power OK',
    }
  }

  const { icon, className, tooltip } = getContent()

  return (
    <span className="relative group">
      <span className={cn('inline-flex items-center justify-center w-5 h-5 rounded-full cursor-help', className)}>
        <FontAwesomeIcon icon={icon} className="h-3 w-3" />
      </span>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
        {tooltip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  )
}
